import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { Label } from "./label";
import { desc } from "framer-motion/client";

const inputVariants = cva(
    "flex h-10 w-full rounded-md bg-transparent px-3 py-2 text-sm ring-offset-transparent file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-copy-lighter focus-visible:placeholder:text-copy-lighter focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-transparent focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50 border focus-visible:border-primary transition-colors duration-300",
    {
        variants: {
            variant: {
                default: "",
                card: "border-transparent bg-accent text-foreground",
                error: "border-destructive",
                success: "border-success",
                cc: "bg-accent focus-visible:border-muted",
            },
            size: {
                default: "h-10 px-3 py-2",
                sm: "h-8 px-2 py-1 text-xs",
                lg: "h-12 px-4 py-3 text-lg",
            },
        },
        defaultVariants: {
            variant: "default",
            size: "default",
        },
    }
);

export interface InputProps
    extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size">,
        VariantProps<typeof inputVariants> {
    label?: string;
    labelIcon?: React.ReactNode;
    description?: string;
    parentClassName?: string;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
    (
        {
            className,
            variant,
            size,
            type,
            label,
            labelIcon,
            description,
            parentClassName,
            ...props
        },
        ref
    ) => {
        return (
            <div className={cn("w-full space-y-1", parentClassName)}>
                {label && (
                    <Label variant={"toggle"} icon={labelIcon} htmlFor={label}>
                        {label}
                    </Label>
                )}
                <input
                    type={type}
                    className={cn(inputVariants({ variant, size, className }))}
                    ref={ref}
                    id={label}
                    {...props}
                />
                {description && (
                    <Label variant={"subtoggle"} htmlFor={label}>
                        {description}
                    </Label>
                )}
            </div>
        );
    }
);
Input.displayName = "Input";

export { Input, inputVariants };
